import { html, nothing, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';
import { PopBaseComponent } from '../../base/pop-base-component.js';
import { Constants } from './sector-attribution-table.constants.js';
import type { SectorAttributionTableContentObject } from './sector-attribution-table.model.js';
import { formatToNDecimalPlaces } from '../../common/utils/formatNumber';
import { sortByNumericProperty } from '../../common/utils/arraySort';

import style from './sector-attribution-table.scss?inline';

/**
 * @tag pop-sector-attribution-table-component
 * @attribute { SectorAttributionTableContentObject } [data-component-object] SectorAttributionTable content object
 */
@customElement(Constants.TAG_NAME)
export class PopSectorAttributionTableComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * SectorAttributionTable content object
   */
  componentObject?: SectorAttributionTableContentObject;

  private renderTableHead() {
    return html`
      <tr>
        <th id="sector-column" scope="col">
          <beacon-table-header variant="normal" sortable="true">
            ${Constants.SECTOR_COLUMN_HEADER}
          </beacon-table-header>
        </th>
        <th
          id="total-value-added-column"
          scope="col"
          class="right-align sorted"
        >
          <beacon-table-header
            variant="normal"
            sortable="true"
            sort-direction="descending"
          >
            ${Constants.TOTAL_VALUE_ADDED_COLUMN_HEADER}
          </beacon-table-header>
        </th>
        <th id="criteria-weight-value-column" scope="col" class="right-align">
          <beacon-table-header variant="normal" sortable="true">
            ${Constants.VALUE_FROM_SECTOR_WEIGHT_COLUMN_HEADER}
          </beacon-table-header>
        </th>
        <th id="stock-selection-value-column" scope="col" class="right-align">
          <beacon-table-header variant="normal" sortable="true">
            ${Constants.VALUE_FROM_STOCK_SELECTION_COLUMN_HEADER}
          </beacon-table-header>
        </th>
      </tr>
    `;
  }

  private renderTableBody() {
    const items = this.componentObject?.items || [];
    const sectors = items.filter((item) => !item.isTotalForAllSectors);

    // Ensure default sort state is applied on initial render
    // After this the Beacon table will manage sort state depending
    // on the user interactions
    const sortedByTotalValueAdded = sortByNumericProperty(
      sectors,
      'totalValueAddedPercent',
      'desc',
    );

    return html`
      ${sortedByTotalValueAdded?.map((item) => {
        const name = item.name || Constants.EMPTY_CELL_VALUE;
        const totalValueAdded = formatToNDecimalPlaces(
          item.totalValueAddedPercent,
          2,
        );
        const valueFromCriteriaWeight = formatToNDecimalPlaces(
          item.criteriaWeightValuePercent,
          2,
        );
        const valueFromStockSelection = formatToNDecimalPlaces(
          item.stockSelectionValuePercent,
          2,
        );

        return html`
          <tr>
            <th>
              <beacon-table-stub>
                <span
                  class="b-typography-sans-serif--body--100 b-font-weight-bold"
                >
                  ${name}
                </span>
              </beacon-table-stub>
            </th>
            <td>
              <beacon-table-record
                variant="numeral"
                sort-value="${item.totalValueAddedPercent}"
                sorted="true"
              >
                ${totalValueAdded}
              </beacon-table-record>
            </td>
            <td>
              <beacon-table-record
                variant="numeral"
                sort-value="${item.criteriaWeightValuePercent}"
              >
                ${valueFromCriteriaWeight}
              </beacon-table-record>
            </td>
            <td>
              <beacon-table-record
                variant="numeral"
                sort-value="${item.stockSelectionValuePercent}"
              >
                ${valueFromStockSelection}
              </beacon-table-record>
            </td>
          </tr>
        `;
      })}
    `;
  }

  private renderTableFooter() {
    const items = this.componentObject?.items || [];
    const total = items.find((item) => item.isTotalForAllSectors);

    if (!total) {
      return nothing;
    }

    return html`
      <tfoot>
        <tr>
          <th scope="row">
            <beacon-table-stub
              ><span
                class="b-typography-sans-serif--body--100 b-font-weight-bold"
                >${total.name}</span
              ></beacon-table-stub
            >
          </th>
          <td>
            <beacon-table-record variant="numeral"
              ><span class="b-font-weight-bold"
                >${formatToNDecimalPlaces(
                  total.totalValueAddedPercent,
                  2,
                )}</span
              ></beacon-table-record
            >
          </td>
          <td>
            <beacon-table-record variant="numeral"
              ><span class="b-font-weight-bold"
                >${formatToNDecimalPlaces(
                  total.criteriaWeightValuePercent,
                  2,
                )}</span
              ></beacon-table-record
            >
          </td>
          <td>
            <beacon-table-record variant="numeral"
              ><span class="b-font-weight-bold"
                >${formatToNDecimalPlaces(
                  total.stockSelectionValuePercent,
                  2,
                )}</span
              ></beacon-table-record
            >
          </td>
        </tr>
      </tfoot>
    `;
  }

  render() {
    return this.renderComponent(html`
      <section class="sector-attribution-table-content">
        <beacon-table variant="fixed" sortable="true">
          <table>
            <thead>
              ${this.renderTableHead()}
            </thead>
            <tbody>
              ${this.renderTableBody()}
            </tbody>
            ${this.renderTableFooter()}
          </table>
        </beacon-table>
      </section>
    `);
  }
}
